import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Clock, CheckCircle2, User, FileText, MessageSquare } from 'lucide-react';
import EngineerSidebar from '../Sidebar/EngineerSidebar';
import { useToast } from '../../hooks/use-toast';
import { parseUTC } from '../../lib/date';

const EngineerHistory = () => {
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [consultings, setConsultings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'orders', 'consultings'

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load orders
      const { ordersAPI } = await import('../../api');
      const ordersData = await ordersAPI.getOrders();
      setOrders(ordersData);

      // Load consultings
      const consultingsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/consulting/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const consultingsData = await consultingsResponse.json();
      setConsultings(consultingsData);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger l'historique",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
      case 'termine':
        return <Badge className="bg-green-500/20 text-green-500 border-green-500/30"><CheckCircle2 className="h-3 w-3 mr-1" />Terminé</Badge>;
      case 'in_progress':
      case 'en_cours':
        return <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30"><Clock className="h-3 w-3 mr-1" />En cours</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-500 border-gray-500/30"><Clock className="h-3 w-3 mr-1" />En attente</Badge>;
    }
  };

  // Merge and filter data
  const allItems = [
    ...orders.map(order => ({ ...order, type: 'order' })),
    ...consultings.map(consulting => ({ ...consulting, type: 'consulting' }))
  ].sort((a, b) => parseUTC(b.created_at) - parseUTC(a.created_at));

  const filteredItems = allItems.filter(item => {
    if (filter === 'orders') return item.type === 'order';
    if (filter === 'consultings') return item.type === 'consulting';
    return true; // 'all'
  });

  return (
    <div className="min-h-screen bg-[#111111]">
      <EngineerSidebar />
      <div className="lg:ml-64 pt-24 pb-12 px-3 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">
              <span className="text-[#C00000]">Historique</span> des services
            </h1>
            <p className="text-[#A6A6A6]">Toutes les commandes et consultings traités</p>
            
            {/* Filter buttons */}
            <div className="flex gap-3 mt-6">
              <Button
                onClick={() => setFilter('all')}
                variant={filter === 'all' ? 'default' : 'outline'}
                className={filter === 'all' 
                  ? 'bg-[#C00000] hover:bg-[#C00000]/90' 
                  : 'border-[#A6A6A6] text-white hover:bg-[#A6A6A6]/10'}
              >
                Tout ({allItems.length})
              </Button>
              <Button
                onClick={() => setFilter('orders')}
                variant={filter === 'orders' ? 'default' : 'outline'}
                className={filter === 'orders' 
                  ? 'bg-[#C00000] hover:bg-[#C00000]/90' 
                  : 'border-[#A6A6A6] text-white hover:bg-[#A6A6A6]/10'}
              >
                <FileText className="h-4 w-4 mr-2" />
                Tuning Files ({orders.length})
              </Button>
              <Button
                onClick={() => setFilter('consultings')}
                variant={filter === 'consultings' ? 'default' : 'outline'}
                className={filter === 'consultings' 
                  ? 'bg-blue-500 hover:bg-blue-500/90' 
                  : 'border-blue-500 text-blue-400 hover:bg-blue-500/10'}
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                Consultings ({consultings.length})
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-[#A6A6A6]">Chargement...</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardContent className="py-12 text-center">
                <p className="text-[#A6A6A6] text-lg">Aucun élément pour le moment</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredItems.map((item) => (
                <Card 
                  key={item.type === 'order' ? item.order_id : item.consulting_id} 
                  className={`bg-[#1a1a1a] border-[#A6A6A6]/20 hover:border-${item.type === 'consulting' ? 'blue' : '[#C00000]'}-500/50 transition-all ${item.type === 'consulting' ? 'border-l-4 border-l-blue-500' : ''}`}
                >
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <CardTitle className="text-white">
                            {item.type === 'order' 
                              ? `${item.vehicle_info.brand} ${item.vehicle_info.model} ${item.vehicle_info.engine}`
                              : `${item.vehicle_info.brand} ${item.vehicle_info.model}`
                            }
                          </CardTitle>
                          {item.type === 'consulting' && (
                            <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                              <MessageSquare className="h-3 w-3 mr-1" />
                              Consulting
                            </Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-3 text-sm text-[#A6A6A6]">
                          <span>{item.type === 'order' ? `Tuning File ${item.order_id}` : `Consulting ${item.consulting_id}`}</span>
                          <span>•</span>
                          {item.type === 'order' && <span>{item.service}</span>}
                          {item.type === 'consulting' && <span>Diagnostic</span>}
                          <span>•</span>
                          <span>{parseUTC(item.created_at).toLocaleDateString('fr-FR')}</span>
                        </div>
                      </div>
                      {getStatusBadge(item.status)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-[#A6A6A6] text-sm mb-1 flex items-center gap-1">
                          <User className="h-3 w-3" />
                          Client
                        </p>
                        <p className="text-white font-semibold">{item.client_name}</p>
                      </div>
                      <div>
                        <p className="text-[#A6A6A6] text-sm mb-1">Prix</p>
                        <p className="text-white font-semibold">{item.price}€ HT</p>
                      </div>
                      <div>
                        <p className="text-[#A6A6A6] text-sm mb-1">Ingénieur assigné</p>
                        <p className={`font-semibold ${item.type === 'consulting' ? 'text-blue-400' : 'text-[#C00000]'}`}>
                          {item.engineer_name || 'Non assigné'}
                        </p>
                      </div>
                      <div>
                        <p className="text-[#A6A6A6] text-sm mb-1">
                          {item.type === 'order' ? 'Fichiers' : 'Documents'}
                        </p>
                        {item.type === 'order' ? (
                          <div className="text-sm">
                            <p className="text-white">
                              {item.original_file_name ? '✓ Original' : '✗ Original'}
                            </p>
                            <p className="text-white">
                              {item.modified_file_name ? '✓ Modifié' : '✗ Modifié'}
                            </p>
                          </div>
                        ) : (
                          <div className="text-sm">
                            <p className="text-blue-400">
                              {item.files?.length || 0} fichier(s)
                            </p>
                            <p className="text-blue-400">
                              {item.links?.length || 0} lien(s)
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    {item.type === 'consulting' && item.problem && (
                      <div className="mt-4 pt-4 border-t border-[#A6A6A6]/20">
                        <p className="text-[#A6A6A6] text-sm mb-1">Problème signalé :</p>
                        <p className="text-white text-sm">{item.problem}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EngineerHistory;
